import { supabase } from '../helpers/Supabase';
import { DelegateInvite } from '../types/delegates';

export class EmailService {
  // Send delegate invitation email using Supabase Auth
  static async sendDelegateInvite(
    invite: DelegateInvite,
    clientName: string,
    storeNames?: string[],
    inviteUrl?: string
  ): Promise<{ success: boolean; error?: string; userId?: string }> {
    try {
      // Use Supabase Auth's signUp method to send invitation email
      const { data, error } = await supabase.auth.signUp({
        email: invite.email,
        password: this.generateTemporaryPassword(), // Generate a temporary password
        options: {
          data: {
            // Custom metadata for the invitation
            invitation_type: 'delegate',
            client_name: clientName,
            delegate_name: invite.name,
            delegate_role: invite.role,
            store_names: storeNames || [],
            delegate_permissions: invite.permissions,
            invited_at: new Date().toISOString(),
            // Add any other metadata you need
          },
          emailRedirectTo: inviteUrl || `${window.location.origin}/delegate-signup`,
          // Customize the email template (if you have email templates set up)
        }
      });

      if (error) {
        console.error('Supabase Auth signup error:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Delegate invitation sent via Supabase Auth:', {
        email: invite.email,
        clientName,
        storeNames,
        userId: data.user?.id
      });

      return { success: true, userId: data.user?.id };
    } catch (error: any) {
      console.error('Email sending failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Generate a temporary password for the invitation
  private static generateTemporaryPassword(): string {
    // Generate a secure random password
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }

  // Alternative: Use Supabase Auth's admin functions (if you have admin access)
  static async sendDelegateInviteAdmin(
    invite: DelegateInvite,
    clientName: string,
    storeNames?: string[],
    inviteUrl?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // This requires admin privileges and the admin client
      // You'd need to set this up in a secure backend/Edge Function
      const { data, error } = await supabase.auth.admin.generateLink({
        type: 'signup',
        email: invite.email,
        password: this.generateTemporaryPassword(),
        options: {
          data: {
            invitation_type: 'delegate',
            client_name: clientName,
            delegate_name: invite.name,
            delegate_role: invite.role,
            store_names: storeNames || [],
            delegate_permissions: invite.permissions,
          },
          redirectTo: inviteUrl || `${window.location.origin}/delegate-signup`,
        }
      });

      if (error) {
        console.error('Supabase Auth admin error:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ Delegate invitation link generated:', data.properties.action_link);
      return { success: true };
    } catch (error: any) {
      console.error('Admin invitation failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Send reminder email for pending invites
  static async sendReminderEmail(
    delegateEmail: string,
    clientName: string,
    inviteUrl: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Use Supabase Auth's resetPassword for reminders
      const { error } = await supabase.auth.resetPasswordForEmail(delegateEmail, {
        redirectTo: inviteUrl
      });

      if (error) {
        return { success: false, error: error.message };
      }

      console.log('✅ Reminder email sent via Supabase Auth');
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }
} 